/**
 * ReportsMenu — a summarized catalog of every report & brief a user can
 * generate in Resmo. Rendered on the Welcome page (once a resume exists)
 * and on the History dashboard.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Target, FileText, BookOpenCheck, Globe, Mic2, HelpCircle,
  BadgeDollarSign, Compass, Footprints, Zap, ChevronDown, ChevronUp, LayoutGrid,
} from "lucide-react";

const CATALOG = [
  {
    group: "Apply",
    color: "#FF6A00",
    items: [
      { icon: Target, name: "Job Fit Report", desc: "Fit score, the role's real problem, tailored resume + cover letter.", route: "/feed-job", testId: "menu-fit-report" },
      { icon: FileText, name: "Role Strategy Brief", desc: "One-page strategic briefing for a specific application.", route: "/strategy-brief", testId: "menu-strategy-brief" },
      { icon: Zap, name: "Opportunity Decoder", desc: "Free JD decode — what the role really wants, shareable.", route: "/challenge", testId: "menu-decoder" },
    ],
  },
  {
    group: "Interview",
    color: "#1E40AF",
    items: [
      { icon: BookOpenCheck, name: "Interview Playbook", desc: "Likely questions + STAR answers pulled from your resume.", route: "/playbooks", testId: "menu-playbook" },
      { icon: Globe, name: "Company Research Brief", desc: "Pre-interview snapshot, talking points and red flags.", route: "/history", testId: "menu-research" },
      { icon: Mic2, name: "Mock Interview Debrief", desc: "Practice out loud, get a scored debrief afterwards.", route: "/mock-interview", testId: "menu-mock" },
      { icon: HelpCircle, name: "Interview Question Bank", desc: "Question set generated for any role or company.", route: "/resources/interview/new", testId: "menu-questions" },
    ],
  },
  {
    group: "Grow",
    color: "#0E5A4A",
    items: [
      { icon: BadgeDollarSign, name: "Salary Assessment", desc: "Market-grounded salary range + what each skill pays.", route: "/skills/new", testId: "menu-salary" },
      { icon: Compass, name: "Role Research", desc: "Any title — day-to-day, skills needed, salary by experience.", route: "/skills/role/new", testId: "menu-role-research" },
      { icon: Footprints, name: "Stepping Stones", desc: "A staged route from your current role to your target one.", route: "/stepping-stones/new", testId: "menu-stones" },
    ],
  },
];

export default function ReportsMenu({ defaultOpen = false, className = "" }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rm-card-static overflow-hidden ${className}`} style={{ background: "white", padding: 0 }} data-testid="reports-menu">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 md:px-5 py-3.5 text-left"
        style={{ background: "#0F1B2D", color: "white" }}
        data-testid="reports-menu-toggle"
      >
        <LayoutGrid size={17} style={{ color: "#FFC145" }} />
        <div className="flex-1 min-w-0">
          <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#FFC145" }}>
            · Report catalog
          </div>
          <div className="font-display uppercase text-[15px] leading-tight">
            Every report Resmo can build for you
          </div>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="p-4 md:p-5 space-y-5">
          {CATALOG.map((g) => (
            <div key={g.group}>
              <div className="font-mono-rm text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: g.color }}>
                · {g.group}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {g.items.map((it) => (
                  <Link
                    key={it.name}
                    to={it.route}
                    className="flex items-start gap-3 p-3 rounded-lg transition-colors hover:bg-slate-50"
                    style={{ border: "1.5px solid #E2E8F0" }}
                    data-testid={it.testId}
                  >
                    <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                         style={{ background: g.color, color: "white" }}>
                      <it.icon size={15} strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-[13px] leading-snug" style={{ color: "#0F1B2D" }}>{it.name}</div>
                      <div className="text-[11.5px] leading-snug mt-0.5" style={{ color: "#64748B" }}>{it.desc}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
